/* Peter Racing: native Shopify facets, accessible drawer and image size guides.
   No API keys, external JS libraries, hidden product filtering or cart overrides. */
(() => {
  'use strict';
  if (window.__prRacingInstalled) return;
  window.__prRacingInstalled = true;
  const STOCK = 'filter.v.availability';
  const mobile = window.matchMedia('(max-width: 991px)');
  const timers = new WeakMap();
  let lastDrawerTrigger = null;
  let lastGuideTrigger = null;
  let backgroundState = [];
  const focusable = 'summary,a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';

  function saveGroups(form) {
    try {
      const state = {};
      form.querySelectorAll('[data-pr-group]').forEach(group => { state[group.dataset.prGroup] = group.open; });
      sessionStorage.setItem('pr-facets:' + new URL(form.action).pathname, JSON.stringify(state));
    } catch (_) { /* Storage may be unavailable in private browsing. */ }
  }

  function restoreGroups(root = document) {
    root.querySelectorAll('form[data-pr-filters]').forEach(form => {
      if (form.dataset.prReady) return;
      form.dataset.prReady = 'true';
      try {
        const state = JSON.parse(sessionStorage.getItem('pr-facets:' + new URL(form.action).pathname) || '{}');
        form.querySelectorAll('[data-pr-group]').forEach(group => {
          if (Object.prototype.hasOwnProperty.call(state, group.dataset.prGroup)) group.open = state[group.dataset.prGroup];
          if (group.querySelector('input:checked')) group.open = true;
        });
      } catch (_) { /* Keep server-rendered defaults. */ }
    });
  }

  function cleanedURL(clearPath) {
    const url = new URL(window.location.href);
    [...url.searchParams.keys()].forEach(key => {
      if (key.startsWith('filter.') || key === 'page' || key === 'view') url.searchParams.delete(key);
    });
    if (clearPath) url.pathname = new URL(clearPath, window.location.origin).pathname;
    url.hash = '';
    return url;
  }

  function validatePrices(form) {
    const min = form.querySelector('[name="filter.v.price.gte"]');
    const max = form.querySelector('[name="filter.v.price.lte"]');
    if (min) min.setCustomValidity('');
    if (min && max && min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
      min.setCustomValidity(form.dataset.priceError || 'Invalid price range.');
    }
    form.querySelectorAll('input:invalid').forEach(input => {
      const group = input.closest('details');
      if (group) group.open = true;
    });
    return form.reportValidity();
  }

  function applyFilters(form, forceStock = false) {
    clearTimeout(timers.get(form));
    if (form.dataset.prNavigating) return;
    if (!validatePrices(form)) return;
    const url = cleanedURL();
    for (const [key, rawValue] of new FormData(form)) {
      const value = String(rawValue).trim();
      if (!value) continue;
      if (key.startsWith('filter.')) url.searchParams.append(key, value);
      else if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    }
    if (forceStock) url.searchParams.set(STOCK, '1');
    saveGroups(form);
    form.dataset.prNavigating = 'true';
    form.setAttribute('aria-busy', 'true');
    document.querySelector('[data-pr-catalog]')?.setAttribute('aria-busy', 'true');
    const status = form.querySelector('[data-pr-filter-status]');
    if (status) status.hidden = false;
    window.location.assign(url.toString());
  }

  function setBackgroundInert(panel, active) {
    if (!active) {
      backgroundState.forEach(([node, old]) => { node.inert = old; });
      backgroundState = [];
      return;
    }
    // Modal is nested in the page. Inert siblings at every ancestor level,
    // while keeping the panel and its clickable backdrop accessible.
    let current = panel;
    while (current && current !== document.body) {
      const parent = current.parentElement;
      if (!parent) break;
      [...parent.children].forEach(sibling => {
        if (sibling === current || sibling.matches('script,style,link,[data-pr-filter-close]')) return;
        backgroundState.push([sibling, sibling.inert]);
        sibling.inert = true;
      });
      current = parent;
    }
  }

  function closeDrawer(restoreFocus = true) {
    const panel = document.querySelector('[data-pr-filter-panel]');
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.removeAttribute('role');
    panel.removeAttribute('aria-modal');
    document.body.classList.remove('pr-filter-open');
    document.querySelectorAll('.pr-filter-backdrop').forEach(el => { el.hidden = true; });
    document.querySelectorAll('[data-pr-filter-open]').forEach(el => el.setAttribute('aria-expanded', 'false'));
    setBackgroundInert(panel, false);
    panel.inert = mobile.matches;
    if (mobile.matches) panel.setAttribute('aria-hidden', 'true');
    else panel.removeAttribute('aria-hidden');
    if (restoreFocus && lastDrawerTrigger && mobile.matches) lastDrawerTrigger.focus();
  }

  function openDrawer(trigger) {
    const panel = document.querySelector('[data-pr-filter-panel]');
    if (!panel || !mobile.matches) return;
    lastDrawerTrigger = trigger;
    panel.inert = false;
    panel.removeAttribute('aria-hidden');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.classList.add('is-open');
    document.querySelectorAll('.pr-filter-backdrop').forEach(el => { el.hidden = false; });
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('pr-filter-open');
    setBackgroundInert(panel, true);
    requestAnimationFrame(() => panel.querySelector('[data-pr-filter-close]')?.focus());
  }

  function closeGuide(dialog) {
    if (dialog?.open) dialog.close();
  }

  document.addEventListener('change', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-pr-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      url.searchParams.delete('view');
      window.location.assign(url.toString());
      return;
    }
    if (!target.matches('input[type="checkbox"][data-pr-facet]')) return;
    const form = target.closest('form[data-pr-filters]');
    if (!form) return;
    const auto = target.checked && target.closest('[data-pr-auto="true"]') && form.dataset.autoStock === 'true';
    if (auto) {
      form.querySelectorAll(`input[name="${STOCK}"]`).forEach(input => {
        input.checked = input.value === '1';
        // Availability counts can be zero before the new OR filter is applied.
        if (input.value === '1') input.disabled = false;
      });
      const stockGroup = form.querySelector(`[data-pr-group="${STOCK}"]`);
      if (stockGroup) stockGroup.open = true;
      form.dataset.prForceStock = 'true';
    } else if (target.name === STOCK) {
      delete form.dataset.prForceStock;
    }
    clearTimeout(timers.get(form));
    timers.set(form, setTimeout(() => applyFilters(form, form.dataset.prForceStock === 'true'), 180));
  });

  document.addEventListener('input', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const form = input.closest('form[data-pr-filters]');
    if (!form) return;
    form.querySelector('[name="filter.v.price.gte"]')?.setCustomValidity('');
    if (input.matches('[data-pr-filter-search]')) {
      const term = input.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
      const group = input.closest('[data-pr-group]');
      let visible = 0;
      group.querySelectorAll('[data-pr-option]').forEach(item => {
        const label = (item.dataset.prLabel || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
        item.hidden = !label.includes(term);
        if (!item.hidden) visible++;
      });
      const empty = group.querySelector('[data-pr-no-options]');
      if (empty) empty.hidden = visible > 0;
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-pr-filters]')) return;
    event.preventDefault();
    applyFilters(form, form.dataset.prForceStock === 'true');
  });

  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const opener = target.closest('[data-pr-filter-open]');
    if (opener) { event.preventDefault(); openDrawer(opener); return; }
    if (target.closest('[data-pr-filter-close]')) { event.preventDefault(); closeDrawer(); return; }
    if (target.closest('[data-pr-guide-close]')) { closeGuide(target.closest('dialog')); return; }
    if (target.matches('dialog.pr-guide-dialog')) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeGuide(target);
      return;
    }
    const guideTrigger = target.closest('[data-pr-guide-open]');
    if (guideTrigger && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      const dialog = document.getElementById(guideTrigger.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        lastGuideTrigger = guideTrigger;
        if (!dialog.open) dialog.showModal();
        document.body.classList.add('pr-dialog-open');
        dialog.querySelector('[data-pr-guide-close]')?.focus();
      }
      return;
    }
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const clear = target.closest('a[data-pr-clear]');
    if (clear) { event.preventDefault(); window.location.assign(cleanedURL(clear.href).toString()); }
  });

  document.addEventListener('close', event => {
    if (!(event.target instanceof Element) || !event.target.matches('.pr-guide-dialog')) return;
    document.body.classList.remove('pr-dialog-open');
    if (lastGuideTrigger?.isConnected) lastGuideTrigger.focus();
  }, true);

  document.addEventListener('keydown', event => {
    const panel = document.querySelector('[data-pr-filter-panel].is-open');
    if (!panel) return;
    if (event.key === 'Escape') { event.preventDefault(); closeDrawer(); return; }
    if (event.key !== 'Tab') return;
    const items = [...panel.querySelectorAll(focusable)].filter(el => el.getClientRects().length > 0 && !el.closest('[hidden]'));
    const first = items[0], last = items[items.length - 1];
    if (!first) { event.preventDefault(); panel.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  function init() { restoreGroups(); closeDrawer(false); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  mobile.addEventListener('change', () => closeDrawer(false));
  document.addEventListener('shopify:section:load', event => { restoreGroups(event.target); closeDrawer(false); });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('form[data-pr-filters]').forEach(form => {
      delete form.dataset.prNavigating;
      delete form.dataset.prForceStock;
      form.removeAttribute('aria-busy');
      const status = form.querySelector('[data-pr-filter-status]');
      if (status) status.hidden = true;
    });
    document.querySelector('[data-pr-catalog]')?.removeAttribute('aria-busy');
  });
})();
